import { useCallback, useEffect, useState } from 'react';

import { isMatch, negate } from 'lodash/fp';

import { ResultUtxo } from '@/networks/btc-like/common';
import { useBtcLikeWalletUtxos } from '@/networks/btc-like/hooks';
import { BtcLikeCurrency } from '@/types';

const utxoSortFn = (utxos: ResultUtxo[]): ResultUtxo[] =>
  utxos
    .sort((a, b) => {
      return b.value - a.value;
    })
    .sort((a, b) => {
      return Number(b.status.confirmed) - Number(a.status.confirmed);
    });

export const useBtcLikeUtxosSelect = (token: BtcLikeCurrency, walletAddress: string | null) => {
  const { data: allUtxos = [], dataUpdatedAt } = useBtcLikeWalletUtxos(token, walletAddress, utxoSortFn);
  const [selectedUtxos, setSelectedUtxos] = useState<ResultUtxo[]>([]);

  useEffect(() => {
    setSelectedUtxos((prev) => {
      return prev.filter(({ txid, vout }) => allUtxos.some(isMatch({ txid, vout })));
    });
  }, [dataUpdatedAt]);

  const confirmedUtxos = allUtxos.filter((utxo) => utxo.status.confirmed);
  const unconfirmedUtxos = allUtxos.filter((utxo) => !utxo.status.confirmed);

  const toggleUtxo = useCallback((utxo: ResultUtxo) => {
    setSelectedUtxos((prev) => {
      const { txid, vout } = utxo;

      const isSelected = prev.some(isMatch({ txid, vout }));

      if (isSelected) {
        return prev.filter(negate(isMatch({ txid, vout })));
      }

      return [...prev, utxo];
    });
  }, []);

  const resetUtxos = useCallback((initialUtxos?: ResultUtxo[]) => {
    setSelectedUtxos(initialUtxos || []);
  }, []);

  return {
    confirmedUtxos,
    unconfirmedUtxos,
    selectedUtxos,
    toggleUtxo,
    resetUtxos,
  };
};
